import React, { useState, useEffect, useCallback, memo } from 'react'
import { Col, Row } from 'react-grid-system'
import classNames from 'classnames'
import { Image, KeyValue, Svg } from '../../'
import { format } from 'date-fns'
import * as styles from './itemCards.module.scss'
import { ToolTypes } from '../../../../types/index'
import { useSettings } from '../../../../hooks'
import { formatLocaleDate } from '../../../../utils/datetime'
import { ItemCardsProps } from './types'
import InfoTooltip from '../../infoTooltip/infoTooltip'

const ItemCards = ({ data, setBypass, getItem }: ItemCardsProps) => {
   const { settings } = useSettings()
   const [itemData, setItemData] = useState(data)

   useEffect(() => {
      setItemData(data)
   }, [data])

   const onCardClick = useCallback(
      (item) => {
         setBypass(false)
         getItem(
            item.Id,
            item.ToolLoanId,
            item.KitId,
            item.Warehouse?.ToolWarehouseID
         )
      },
      [getItem, setBypass]
   )

   const renderCardLocation = useCallback(
      (item) => (
         <>
            <span>
               {item?.DefaultLocation === item?.CurrentLocation ? (
                  <Svg
                     id="warehouse"
                     height="34"
                     width="34"
                     className={styles.warehouseIcon}
                  />
               ) : (
                  <Svg
                     className={styles.locationIcon}
                     id="location"
                     width="16"
                  />
               )}
            </span>
            <span>{item?.CurrentLocation}</span>
         </>
      ),
      []
   )

   return (
      <>
         {itemData?.length > 0 &&
            itemData.map((item: any, index) => (
               // need to use index as key here due to duplicate ids coming from the API
               <Col key={index} xs={12} xl={6} className={styles.column}>
                  <button
                     className={classNames(styles.itemCard, {
                        [styles[item.StatusDesc.toLowerCase()]]: true,
                     })}
                     onClick={() => onCardClick(item)}
                  >
                     <div className={styles.itemCard_content}>
                        <div className={styles.itemCard_top}>
                           <h3 className={styles.itemCard_header}>
                              {item.Title}
                              <small className={styles.itemCard_subheader}>
                                 {item.ModelNumber && item.Manufacturer
                                    ? `${item.Manufacturer} | ${item.ModelNumber}`
                                    : item.Manufacturer}
                              </small>
                           </h3>

                           <div className={styles.itemCard_status}>
                              <div className={styles.itemCard_statusLabel}>
                                 {[2, 4].includes(item.ToolType)
                                    ? `${item.StatusQuantity} `
                                    : ''}
                                 {item.StatusDesc}
                              </div>

                              <div className={styles.itemCard_icons}>
                                 {
                                    item.Comments && (
                                       <span title="Action Comment" className={styles.itemCard_commentTooltip}>
                                          <InfoTooltip
                                             openIcon='comment-filled'
                                             closedIcon='comment-outlined'
                                             ariaLabel='Action Comment'
                                             content={item.Comments}
                                             inlineStyle={{ 'bottom': '0px', left: '7px' }}
                                          />
                                       </span>
                                    )
                                 }
                                 {item.GPS && (
                                    <span title="Location Tracking">
                                       <Svg
                                          id="locations"
                                          width={16}
                                          className={
                                             styles.locationTrackingIcon
                                          }
                                          overrideViewBox="0 0 16 17"
                                       />
                                    </span>
                                 )}
                                 {item.Notifications > 0 && (
                                    <span title="Tasks">
                                       <Svg id="tasks" width={18} />
                                    </span>
                                 )}
                                 {item.NoteCount > 0 && (
                                    <span title="Notes">
                                       <Svg id="notes" width={15} />
                                    </span>
                                 )}
                              </div>
                           </div>
                        </div>

                        <div className={styles.itemCard_image}>
                           <Image
                              width={100}
                              url={item.PictureURL}
                              alt={item.title}
                              defaultImagePadding={0}
                           />
                        </div>
                        <div className={styles.itemCard_info}>
                           <Row gutterWidth={15}>
                              <Col xs={6} xxl={4}>
                                 <KeyValue
                                    className={styles.itemCard_keyValue}
                                    label="Category"
                                    value={item.Category}
                                    svgId="cardViewCategory"
                                 />
                              </Col>
                              <Col xs={6} xxl={4}>
                                 <KeyValue
                                    className={styles.itemCard_keyValue}
                                    label="Barcode"
                                    value={item.Barcode}
                                    svgId="cardViewBarcode"
                                 />
                              </Col>
                              <Col xs={6} xxl={4}>
                                 <KeyValue
                                    className={styles.itemCard_keyValue}
                                    label="Serial number"
                                    value={item.SerialNumber}
                                    svgId="cardViewSerialNumber"
                                 />
                              </Col>
                              {settings.KitEnabled &&
                                 item.ToolType !== ToolTypes.Kit && (
                                    <Col xs={6} xxl={4}>
                                       <KeyValue
                                          className={styles.itemCard_keyValue}
                                          label="Assigned to Kit"
                                          value={item.AssignedToKitName}
                                          svgId="cardViewAssignedToKit"
                                       />
                                    </Col>
                                 )}
                              {settings.CustomText1Enabled && (
                                 <Col xs={6} xxl={4}>
                                    <KeyValue
                                       className={styles.itemCard_keyValue}
                                       label={settings.CustomText1Label}
                                       value={item.CustomText1Value}
                                       svgId="cardViewText"
                                    />
                                 </Col>
                              )}

                              <Col xs={6} xxl={4}>
                                 {item?.GPS && (
                                    <KeyValue
                                       className={styles.itemCard_keyValue}
                                       label={'Last GPS Scan'}
                                       value={formatLocaleDate(
                                          item.GPS.CreatedOn
                                       )}
                                       svgId="cardViewGps"
                                    />
                                 )}

                                 {item?.Bluetooth && (
                                    <KeyValue
                                       className={styles.itemCard_keyValue}
                                       label={'Last Bluetooth Ping'}
                                       value={item.CustomText1Value}
                                       svgId="cardViewBluetooth"
                                    />
                                 )}
                              </Col>
                           </Row>
                        </div>
                     </div>
                     <div className={styles.itemCard_footer}>
                        <div className={styles.itemCard_owner}>
                           <span>
                              <Svg id="assigned to" />
                           </span>
                           <span>
                              {item?.AssignedToUser ?? item?.OwnerName}
                           </span>
                        </div>
                        <div className={styles.itemCard_locationDue}>
                           {item.StatusDesc === 'Loaned' && (
                              <div className={styles.itemCard_dueBack}>
                                 <span>
                                    <Svg id="return tool" />
                                 </span>
                                 <span title="Date due back">
                                    {`Due ${
                                       item.DueBackDate ===
                                          '2099-12-31T00:00:00' ||
                                       item.DueBackDate === null
                                          ? '-'
                                          : format(
                                               new Date(item.DueBackDate),
                                               'MM/dd/yyyy'
                                            )
                                    }`}
                                 </span>
                              </div>
                           )}

                           <div className={styles.itemCard_location}>
                              {settings.EnableLocationTracking &&
                                 item?.CurrentLocation &&
                                 renderCardLocation(item)}
                           </div>
                        </div>
                     </div>
                  </button>
               </Col>
            ))}
      </>
   )
}

export default memo(ItemCards)
